import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { listWorkSessions } from '../../../api/workSessionsApi';
import { approveCarStatus, assignCarResponsible, getCar, getCarStatusHistory } from '../../../api/carsApi';
import { fetchCollaborators, unassignResponsible } from '../../../api/collaboratorsApi';
import { getColaboradoresElegiveis } from '../../../api/especialidadesApi';
import { buscarServicosPendentes, mensagemConfirmacaoEntrega } from '../../../utils/servicosPendentes';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { ESTEIRA } from '../../../shared/esteiras';
import { notify } from '../../../shared/feedback/notify';
import {
  useApproveWorkSessionMutation,
  useRejectWorkSessionMutation,
} from '../../../infrastructure/react-query/hooks/useWorkSessionsQueries';
import { WorkSession } from '../../../interfaces/WorkSession';
import { ServicoInacabado } from '../../../interfaces/AtividadeCarro';
import ConfirmModal from '../confirm/ConfirmModal';
import ObservacoesAtendimento from '../../observacoes-atendimento/ObservacoesAtendimento';
import ProgressoSubtarefas from '../../progresso-subtarefas/ProgressoSubtarefas';
import ComentariosDoVeiculo from '../../comentarios/ComentariosDoVeiculo';
import TagsDaEsteira from '../../tags-da-esteira/TagsDaEsteira';
import SeloPosEvento from '../../selo-pos-evento/SeloPosEvento';
import SeloPrazoDaEtapa from '../../selo-prazo/SeloPrazoDaEtapa';
import SeloEsteiraParalela from '../../selo-esteira-paralela/SeloEsteiraParalela';
import ControleParalelasModal from '../controle-paralelas/ControleParalelasModal';

import './AtendimentoDetailsModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface AtendimentoDetailsModalProps {
  carId: string;
  isChefeView: boolean;
  onClose: () => void;
  /** Chamado após aprovar/reprovar horas, para o chamador atualizar a listagem. */
  onChanged?: () => void;
}

const SESSION_STATUS_LABEL: Record<string, string> = {
  IN_PROGRESS: 'Em andamento',
  PAUSED: 'Pausada',
  FINISHED: 'Finalizada',
};

const APPROVAL_STATUS_LABEL: Record<string, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Reprovada',
};

const formatDuration = (seconds?: number | null) => {
  if (!seconds || seconds <= 0) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString('pt-BR') : '-';

const formatDateTime = (value?: string | null) =>
  value
    ? `${new Date(value).toLocaleDateString('pt-BR')} ${new Date(value).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
    : '-';

/**
 * Valor sentinela do select de próximo responsável: desvincular explicitamente.
 *
 * at-93: vive no escopo do módulo (era declarado dentro do componente) porque virou o **valor
 * inicial** do estado — "Não atribuir" é o padrão, e o `useState` roda antes de qualquer
 * declaração interna.
 */
const LEAVE_UNASSIGNED = 'NONE';

const workedSeconds = (session: WorkSession) =>
  Math.max((session.durationInSeconds ?? 0) - (session.totalPausedSeconds ?? 0), 0);

const AtendimentoDetailsModal: React.FC<AtendimentoDetailsModalProps> = ({
  carId,
  isChefeView,
  onClose,
  onChanged,
}) => {
  const { esteirasAtivas, labelDe, chaveDe } = useEsteiras();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [car, setCar] = useState<any>(null);
  const [sessions, setSessions] = useState<WorkSession[]>([]);
  const [statusHistory, setStatusHistory] = useState<any[]>([]);

  // Linha de atividade expandida (accordion) — uma por vez.
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  // Aprovação inline (Chefe) — uma atividade expandida por vez.
  const [approvalSessionId, setApprovalSessionId] = useState<string | null>(null);
  const [approvalEstimatedDays, setApprovalEstimatedDays] = useState('');
  /**
   * at-93: nasce em "Não atribuir" (o sentinela `NONE`), como nos modais de esteira.
   *
   * Era `''` — a opção "Manter os colaboradores atuais" —, e isso fazia a aprovação levar o
   * colaborador atual para a esteira nova por omissão. Pedido do Victor, 07/09/2026.
   */
  const [approvalNextCollaboratorId, setApprovalNextCollaboratorId] = useState(LEAVE_UNASSIGNED);
  const [rejectionReason, setRejectionReason] = useState('');
  const [approvalCollaborators, setApprovalCollaborators] = useState<Array<{ id: string; fullName?: string; username?: string }>>([]);
  const [loadingCollaborators, setLoadingCollaborators] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState<{ message: string; onConfirm: () => void } | null>(null);
  /**
   * at-62: ids que PODEM ser o próximo colaborador (elegíveis para a esteira relevante — destino
   * sugerido quando há mudança pendente, senão a atual). `null` = sem restrição (regra inerte ou
   * consulta falhou, fail-open). Oferecer no seletor alguém que o backend vai recusar é oferecer
   * um caminho fechado.
   *
   * Guarda os PERMITIDOS, não os bloqueados, pelo mesmo motivo do `CollaboratorModal`: assim a
   * marcação não depende de qual fatia da lista está carregada.
   */
  const [idsElegiveisProximo, setIdsElegiveisProximo] = useState<string[] | null>(null);
  // at-99: o selo ao lado da placa abre o controle das paralelas (pedido do Victor, 10/09/2026).
  const [controleParalelasAberto, setControleParalelasAberto] = useState(false);

  const approveMutation = useApproveWorkSessionMutation();
  const rejectMutation = useRejectWorkSessionMutation();

  const loadData = useCallback(async () => {
    try {
      const [carData, sessionsData, historyData] = await Promise.all([
        getCar(carId),
        listWorkSessions({ carId, page: 1, limit: 500 }),
        getCarStatusHistory(carId),
      ]);
      setCar(carData);
      setSessions(sessionsData.sessions || []);
      setStatusHistory((historyData.history as any[]) || []);
      setError(null);
    } catch (error) {
      // BUG-V76: o estado de erro existia mas nunca era renderizado — o modal
      // abria em branco. Agora aparece no corpo do modal e também como aviso.
      setError('Não foi possível carregar os detalhes do atendimento.');
      notify.errorFrom(
        error,
        'Não foi possível carregar os detalhes do atendimento. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setLoading(false);
    }
  }, [carId]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  const totals = useMemo(() => {
    const totalWorked = sessions.reduce((sum, s) => sum + workedSeconds(s), 0);
    const pendingCount = sessions.filter((s) => s.status === 'FINISHED' && s.approvalStatus === 'PENDING').length;

    const bySector = new Map<string, number>();
    sessions.forEach((s) => {
      const sector = s.sector || 'Sem setor';
      bySector.set(sector, (bySector.get(sector) ?? 0) + workedSeconds(s));
    });

    return { totalWorked, pendingCount, bySector: Array.from(bySector.entries()) };
  }, [sessions]);

  const orderedSessions = useMemo(
    () => [...sessions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [sessions],
  );

  const orderedHistory = useMemo(
    () => [...statusHistory].sort((a, b) => new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()),
    [statusHistory],
  );

  const openApproval = async (session: WorkSession) => {
    setApprovalSessionId(session.id);
    setApprovalEstimatedDays('');
    // at-93: cada abertura recomeça no padrão seguro ("Não atribuir"), nunca na escolha anterior.
    setApprovalNextCollaboratorId(LEAVE_UNASSIGNED);
    setRejectionReason('');
    setIdsElegiveisProximo(null);
    let listaDeColaboradores = approvalCollaborators;
    if (session.carPendingStatus || approvalCollaborators.length === 0) {
      setLoadingCollaborators(true);
      try {
        const response = await fetchCollaborators(1, 50);
        listaDeColaboradores = response.users || [];
        setApprovalCollaborators(listaDeColaboradores);
      } catch (error) {
        listaDeColaboradores = [];
        setApprovalCollaborators([]);
        notify.errorFrom(
          error,
          'Não foi possível carregar a lista de colaboradores. Recarregue a página; se persistir, acione a TI.',
        );
      } finally {
        setLoadingCollaborators(false);
      }
    }

    // at-62: a esteira que importa para o PRÓXIMO colaborador é o destino sugerido, quando há
    // mudança pendente — senão, a atual. Fail-open: falha na consulta deixa a lista completa e o
    // backend recusa com 409 se for o caso.
    const destinoSugerido = session.carPendingStatus
      ? esteirasAtivas.find(
          (e) =>
            e.nome === session.carPendingStatus ||
            e.id === session.carPendingStatus ||
            e.chave === session.carPendingStatus,
        )
      : null;
    const esteiraRelevanteId = destinoSugerido?.id ?? car?.esteiraId;
    if (!esteiraRelevanteId || listaDeColaboradores.length === 0) return;

    try {
      // at-99: o `carId` soma as esteiras abertas em paralelo — elas continuam vigentes depois da
      // aprovação, e é contra esse conjunto que o backend julga o vínculo.
      const { regraAtiva, elegiveis } = await getColaboradoresElegiveis(esteiraRelevanteId, carId);
      if (!regraAtiva) return;
      setIdsElegiveisProximo(elegiveis.map((c) => c.id));
    } catch (error) {
      notify.warning(
        'Não foi possível verificar as especialidades dos colaboradores. A lista está completa; ' +
          'se o escolhido não atender a esteira, o sistema recusa ao salvar.',
        { toastId: 'atendimento-elegibilidade' },
      );
    }
  };

  /**
   * Serviços em aberto do veículo (confirmação antes de aprovar rumo à Entrega).
   * at-46: pelo ESTADO do serviço, via helper único — a derivação antiga por período APPROVED
   * dava serviço apenas pausado como realizado e omitia justamente o aviso que importa.
   * at-60: o helper não rejeita; falha de consulta volta como `verificacaoIndisponivel` e a
   * mensagem devolvida declara que não foi possível verificar — antes o Chefe aprovava a
   * Entrega sem confirmação nenhuma, como se o veículo estivesse sem pendências.
   */
  const checkPendingConveyorBelts = async (): Promise<string | null> => {
    const pendentes = await buscarServicosPendentes(carId);
    return mensagemConfirmacaoEntrega(pendentes);
  };

  // GET /cars/:id devolve status = chave da esteira e statusLabel = nome.
  const esteiraAtualLabel = car
    ? (car.status === ESTEIRA.ENTREGA ? 'Entregue' : (car.statusLabel || '-'))
    : '-';

  const sessionHasPendingEsteira = (session: WorkSession) =>
    Boolean(session.carPendingStatus && session.carStatusApprovalStatus === 'PENDING');

  /**
   * Só quem pode trabalhar na esteira relevante (decisão do Victor, 15/09/2026).
   *
   * Emenda a at-62, que os deixava listados e desabilitados com o motivo ao lado. A lista aqui
   * vem paginada em 50 nomes: com a oficina inteira dentro do `<select>`, quem pode ser escolhido
   * fica indistinguível do resto.
   *
   * ⚠️ `null` mantém a lista completa — regra inerte ou consulta que falhou (fail-open que já
   * existia e que o aviso de "não foi possível verificar" declara na tela).
   */
  const colaboradoresOferecidos = useMemo(
    () =>
      idsElegiveisProximo === null
        ? approvalCollaborators
        : approvalCollaborators.filter((c) => idsElegiveisProximo.includes(c.id)),
    [approvalCollaborators, idsElegiveisProximo],
  );

  const leaveUnassigned = approvalNextCollaboratorId === LEAVE_UNASSIGNED;
  const nextCollaborator = !leaveUnassigned && approvalNextCollaboratorId
    ? approvalCollaborators.find((c) => c.id === approvalNextCollaboratorId)
    : null;
  const nextCollaboratorName = nextCollaborator
    ? (nextCollaborator.fullName || nextCollaborator.username || 'o colaborador selecionado')
    : null;

  /**
   * Espelha o guard do backend (at-24, atualizado no at-45): os efeitos de posse/movimentação
   * do aprovar/reprovar só acontecem se o dono das horas ainda está VINCULADO ao carro e não
   * há serviço em andamento de NINGUÉM — senão, as horas se resolvem sem tocar no veículo.
   */
  const atendimentoSeguiuEmFrente = (session: WorkSession) => {
    const activeSessionOnCar = sessions.some(
      (s) => s.id !== session.id && (s.status === 'IN_PROGRESS' || s.status === 'PAUSED'),
    );
    const donoAindaVinculado = Boolean(
      session.collaboratorId && (car?.colaboradores ?? []).some((c: any) => c.id === session.collaboratorId),
    );
    return activeSessionOnCar || !donoAindaVinculado;
  };

  /**
   * at-62: quem sai do veículo se a aprovação mover o carro para a esteira sugerida.
   *
   * ⚠️ Roda ANTES da confirmação, de propósito. `executeApproval` aprova as HORAS primeiro e só
   * depois move o veículo — se o aviso de especialidade só chegasse como 409 do
   * `approveCarStatus`, o Chefe descobriria o efeito com as horas já aprovadas e a mudança de
   * esteira pendente. Perguntar antes é o que mantém a ação atômica aos olhos de quem confirma.
   *
   * `indisponivel = true` quando a consulta falhou: a mensagem declara isso em vez de afirmar
   * que ninguém será desvinculado (erro de API não pode virar afirmação de negócio — at-60).
   */
  const buscarInelegiveisDaAprovacao = async (
    session: WorkSession,
  ): Promise<{ nomes: string[]; indisponivel: boolean }> => {
    if (!sessionHasPendingEsteira(session)) return { nomes: [], indisponivel: false };

    const destino = esteirasAtivas.find(
      (e) =>
        e.nome === session.carPendingStatus ||
        e.id === session.carPendingStatus ||
        e.chave === session.carPendingStatus,
    );
    if (!destino?.id || destino.permiteColaborador === false) {
      // Destino que não aceita colaborador desvincula todos por outro caminho, já descrito
      // na mensagem — não é caso de especialidade.
      return { nomes: [], indisponivel: false };
    }

    try {
      const { regraAtiva, elegiveis } = await getColaboradoresElegiveis(destino.id, carId);
      if (!regraAtiva) return { nomes: [], indisponivel: false };
      const idsElegiveis = new Set(elegiveis.map((c) => c.id));
      const nomes = (car?.colaboradores ?? [])
        .filter((c: any) => !idsElegiveis.has(c.id))
        .map((c: any) => c.fullName ?? 'Colaborador');
      return { nomes, indisponivel: false };
    } catch {
      return { nomes: [], indisponivel: true };
    }
  };

  /**
   * Monta a mensagem de confirmação explicando exatamente o que a aprovação
   * fará com o veículo e o responsável (decisão de 07/2026: toda ação de
   * aprovar/reprovar horas confirma explicando os efeitos).
   */
  const buildApproveMessage = (
    session: WorkSession,
    especialidade?: { nomes: string[]; indisponivel: boolean },
  ) => {
    const plate = car?.plate || session.carPlate || '';
    const collaborator = session.collaboratorName || 'Colaborador';
    const effects: string[] = [];

    if (sessionHasPendingEsteira(session)) {
      const destinoNome = labelDe(session.carPendingStatus ?? '') || session.carPendingStatus;
      const destino = esteirasAtivas.find(
        (e) => e.nome === session.carPendingStatus || e.id === session.carPendingStatus || e.chave === session.carPendingStatus,
      );
      effects.push(`o veículo ${plate} será movido para a esteira ${destinoNome}`);
      const defaultDays = destino?.slaDiasPadrao ?? 4;
      effects.push(
        approvalEstimatedDays
          ? `com prazo estimado de ${approvalEstimatedDays} dia(s) no setor`
          : `com o prazo padrão da esteira (${defaultDays} dia${defaultDays === 1 ? '' : 's'}) no setor`,
      );
      if (nextCollaboratorName) {
        effects.push(`${nextCollaboratorName} será adicionado aos colaboradores do veículo`);
      } else if (leaveUnassigned) {
        effects.push('o veículo ficará sem nenhum colaborador');
      } else if (destino && !destino.permiteColaborador) {
        effects.push('todos os colaboradores serão desvinculados (a esteira destino não permite colaborador)');
      } else {
        effects.push('os colaboradores atuais serão mantidos');
      }
      effects.push('sessões ativas de outros colaboradores neste veículo serão encerradas');
    } else if (session.servicoConcluido === false) {
      // at-46: o colaborador finalizou declarando o serviço INACABADO — aprovar as horas não
      // encerra a etapa, e é isso que a confirmação precisa deixar claro.
      const esteiraServico = labelDe(session.esteiraId ?? '') || session.sector || esteiraAtualLabel;
      effects.push(`as horas serão aprovadas normalmente`);
      effects.push(
        `o serviço de ${esteiraServico} permanece INACABADO: o veículo ${plate} não será movido para Aguardando Próxima Etapa e nenhum colaborador será desvinculado`,
      );
      effects.push(
        'o serviço fica como pendência e pode ser retomado quando o veículo voltar para esta esteira',
      );
      if (nextCollaboratorName) {
        effects.push(`${nextCollaboratorName} será adicionado aos colaboradores do veículo`);
      }
    } else {
      const stillInSessionLane = Boolean(session.esteiraId) && car?.esteiraId === session.esteiraId;
      const seguiuEmFrente = atendimentoSeguiuEmFrente(session);
      if (stillInSessionLane && !seguiuEmFrente) {
        effects.push(`o veículo ${plate} será movido para Aguardando Próxima Etapa`);
      } else if (stillInSessionLane) {
        effects.push(`o veículo ${plate} permanecerá na esteira ${esteiraAtualLabel} com os colaboradores atuais (o atendimento seguiu em frente) — somente as horas serão aprovadas`);
      } else {
        effects.push(`o veículo ${plate} permanecerá na esteira ${esteiraAtualLabel} (ele já foi movido após esta atividade) — somente as horas serão aprovadas`);
      }
      if (nextCollaboratorName) {
        effects.push(`${nextCollaboratorName} será adicionado aos colaboradores do veículo`);
      } else if (leaveUnassigned || (stillInSessionLane && !seguiuEmFrente)) {
        // at-45: a etapa se encerra para o veículo — todos saem.
        effects.push('todos os colaboradores serão desvinculados do veículo');
      }
    }

    // at-62: o desvínculo por especialidade entra na MESMA confirmação — é efeito da aprovação,
    // não um segundo passo.
    if (especialidade?.nomes.length) {
      effects.push(
        `${especialidade.nomes.join(', ')} ${especialidade.nomes.length === 1 ? 'sairá' : 'sairão'} ` +
          'do veículo por não ter a especialidade da esteira de destino, e as horas em aberto ' +
          'irão para aprovação',
      );
    } else if (especialidade?.indisponivel) {
      effects.push(
        'não foi possível verificar as especialidades dos colaboradores agora — se algum deles não ' +
          'atender a esteira de destino, ele será desvinculado do veículo e as horas em aberto irão ' +
          'para aprovação',
      );
    }

    return `Ao aprovar as horas de ${collaborator}: ${effects.join('; ')}. Tem certeza de que deseja aprovar?`;
  };

  const buildRejectMessage = (session: WorkSession) => {
    const plate = car?.plate || session.carPlate || '';
    const collaborator = session.collaboratorName || 'Colaborador';
    /**
     * at-45: a reprovação desvincula APENAS o dono das horas; os demais seguem no veículo.
     *
     * at-93: com "Não atribuir" isso deixa de valer — a escolha é sobre o estado do veículo, e a
     * reprovação passa a esvaziá-lo. Antes da at-93 este ramo não existia porque a opção nunca era
     * o padrão: quem a escolhia e reprovava lia "os demais colaboradores permanecem" e o sistema
     * mantinha os demais mesmo assim — rótulo e efeito discordavam.
     */
    const responsavelEffect = leaveUnassigned
      ? 'todos os colaboradores saem do veículo, que fica sem colaborador'
      : nextCollaboratorName
      ? `${collaborator} será desvinculado e ${nextCollaboratorName} será adicionado aos colaboradores do veículo`
      : atendimentoSeguiuEmFrente(session)
        ? 'os colaboradores do veículo serão mantidos (o atendimento seguiu em frente)'
        : `${collaborator} será desvinculado do veículo — os demais colaboradores permanecem`;
    let message =
      `A reprovação das horas de ${collaborator} fará com que o veículo ${plate} permaneça na esteira ${esteiraAtualLabel} ` +
      `e ${responsavelEffect}. ${collaborator} será notificado da reprovação` +
      `${rejectionReason.trim() ? ' com a justificativa informada' : ''}.`;
    if (sessionHasPendingEsteira(session)) {
      const destinoNome = labelDe(session.carPendingStatus ?? '') || session.carPendingStatus;
      message += ` Atenção: a mudança para a esteira ${destinoNome} solicitada pelo colaborador continuará pendente e deverá ser aprovada ou rejeitada separadamente.`;
    }
    return `${message} Tem certeza de que deseja reprovar?`;
  };

  /**
   * at-68 (BUG-V52 e BUG-V78): a aprovação são até TRÊS chamadas não transacionais, e cada
   * uma reporta o próprio resultado.
   *
   * Antes, um único `try` envolvia aprovar horas -> mover esteira -> vincular colaborador. Se a
   * atribuição falhasse **depois** de as horas já terem sido aprovadas, o catch externo dizia
   * "Não foi possível aprovar as horas" — e o Chefe repetia uma aprovação já efetivada, ou a
   * considerava pendente. É o falso *fracasso*, espelho do falso sucesso que a at-60 atacou
   * (ela já havia isolado o `unassign`; faltavam as outras duas etapas).
   *
   * A regra é a do `handleApproveStatus` da tela do Chefe (at-62): quem falhou, falhou; o que
   * deu certo é afirmado. A ordem importa — a etapa 1 é a única que pode abortar tudo, porque
   * sem horas aprovadas não há resultado parcial a relatar.
   */
  const executeApproval = async (session: WorkSession) => {
    // Etapa 1 — as horas. Falha aqui é falha da ação inteira: nada foi aplicado.
    try {
      await approveMutation.mutateAsync(session.id);
    } catch (err) {
      notify.actionError(err, { action: 'approve', subject: 'horas' });
      return;
    }

    // Etapas 2 e 3 — daqui em diante as horas ESTÃO aprovadas. Nenhuma falha pode ser
    // relatada como "não foi possível aprovar".
    const parciais: string[] = [];

    if (sessionHasPendingEsteira(session)) {
      const days = approvalEstimatedDays ? Number(approvalEstimatedDays) : undefined;
      try {
        // at-62: `confirmarDesvinculoInelegiveis` vai como `true` porque a mensagem que o Chefe
        // acabou de aceitar JÁ descreveu esse efeito.
        await approveCarStatus(session.carId, days, true, true, undefined, true);
      } catch (err) {
        notify.errorFrom(err, 'As horas foram aprovadas, mas a mudança de esteira NÃO foi aplicada.');
        parciais.push('o veículo continua na esteira atual e a mudança segue pendente');
      }
    }

    if (leaveUnassigned) {
      try {
        await unassignResponsible(session.carId);
      } catch (err) {
        notify.errorFrom(err, 'As horas foram aprovadas, mas os colaboradores NÃO foram removidos do veículo.');
        parciais.push('os colaboradores continuam vinculados ao veículo');
      }
    } else if (approvalNextCollaboratorId) {
      try {
        await assignCarResponsible(session.carId, approvalNextCollaboratorId);
      } catch (err) {
        notify.errorFrom(err, 'As horas foram aprovadas, mas o colaborador escolhido NÃO foi adicionado ao veículo.');
        parciais.push('o veículo está sem o colaborador escolhido');
      }
    }

    if (parciais.length > 0) {
      notify.warning(
        `As horas foram aprovadas, mas a ação não terminou: ${parciais.join('; ')}. Confira o veículo antes de seguir.`,
      );
    } else {
      // at-65 (BUG-V51): produzia "Horas aprovadas aprovado com sucesso".
      notify.success('Horas aprovadas com sucesso.');
    }

    setApprovalSessionId(null);
    try {
      await loadData();
    } catch {
      // A ação foi concluída — só a recarga falhou (regra da at-60: warning, não error).
      notify.warning('A ação foi concluída, mas a atualização da tela falhou. Recarregue a página.', {
        toastId: 'atendimento-refresh-pos-aprovacao',
      });
    }
    onChanged?.();
  };

  const handleApprove = async (session: WorkSession) => {
    // at-62: a pré-checagem vem ANTES da confirmação — ver `buscarInelegiveisDaAprovacao`.
    const especialidade = await buscarInelegiveisDaAprovacao(session);

    setConfirmModalData({
      message: buildApproveMessage(session, especialidade),
      onConfirm: async () => {
        setConfirmModalData(null);

        // Destino Entrega com esteiras planejadas não realizadas pede uma
        // confirmação adicional antes de encerrar o atendimento.
        const isEntregaLane = chaveDe(session.carPendingStatus ?? '') === ESTEIRA.ENTREGA;
        if (isEntregaLane && session.carStatusApprovalStatus === 'PENDING') {
          const mensagemPendencia = await checkPendingConveyorBelts();
          if (mensagemPendencia) {
            setConfirmModalData({
              message: mensagemPendencia,
              onConfirm: () => {
                setConfirmModalData(null);
                executeApproval(session);
              },
            });
            return;
          }
        }

        executeApproval(session);
      },
    });
  };

  const handleReject = (session: WorkSession) => {
    setConfirmModalData({
      message: buildRejectMessage(session),
      onConfirm: async () => {
        setConfirmModalData(null);
        // at-68 (BUG-V78): a reprovação tinha o MESMO defeito da aprovação — a atribuição do
        // próximo colaborador ficava no try externo, então falhar nela anunciava "não foi
        // possível reprovar as horas" para horas já reprovadas.
        try {
          await rejectMutation.mutateAsync({ id: session.id, rejectionReason: rejectionReason || undefined });
        } catch (err) {
          notify.actionError(err, { action: 'reject', subject: 'horas' });
          return;
        }

        let atribuicaoFalhou = false;
        let desvinculoFalhou = false;
        // "Próximo responsável" vale também na reprovação (decisão de 07/2026): a reprovação
        // desvincula o atual e o selecionado assume em seguida.
        if (approvalNextCollaboratorId && !leaveUnassigned) {
          try {
            await assignCarResponsible(session.carId, approvalNextCollaboratorId);
          } catch (err) {
            atribuicaoFalhou = true;
            notify.errorFrom(err, 'As horas foram reprovadas, mas o colaborador escolhido NÃO foi adicionado ao veículo.');
          }
        } else if (leaveUnassigned) {
          /**
           * at-93: "Não atribuir" precisa de chamada própria também aqui.
           *
           * O comentário antigo dizia que a reprovação já desvinculava — ela desvincula **só o
           * dono das horas** (at-45), então os demais continuavam no veículo enquanto o rótulo
           * prometia "sem colaborador". Com a opção virando o padrão, o desencontro passaria a ser
           * a regra.
           */
          try {
            await unassignResponsible(session.carId);
          } catch (err) {
            desvinculoFalhou = true;
            notify.errorFrom(err, 'As horas foram reprovadas, mas os colaboradores NÃO foram removidos do veículo.');
          }
        }

        if (atribuicaoFalhou) {
          notify.warning('As horas foram reprovadas e o veículo está sem o colaborador escolhido. Confira o veículo antes de seguir.');
        } else if (desvinculoFalhou) {
          notify.warning('As horas foram reprovadas, mas o veículo continua com os colaboradores anteriores. Confira o veículo antes de seguir.');
        } else {
          // at-65 (BUG-V51): produzia "Horas reprovado com sucesso" — erro de concordância,
          // porque o verbo do helper vem sempre no masculino singular.
          notify.success('Horas reprovadas com sucesso.');
        }

        setApprovalSessionId(null);
        try {
          await loadData();
        } catch {
          notify.warning('A ação foi concluída, mas a atualização da tela falhou. Recarregue a página.', {
            toastId: 'atendimento-refresh-pos-reprovacao',
          });
        }
        onChanged?.();
      },
    });
  };

  return (
    <div className="controle-producao-modal-overlay" onClick={onClose}>
      <div className="controle-producao-modal atendimento-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="controle-producao-modal-header">
          <h3>
            Atendimento — {car?.plate || ''}
            {/* at-89: motivo da pendência ao lado da placa (Victor, 03/09/2026). */}
            {car?.id && (
              <TagsDaEsteira carId={car.id} esteiraAtual={car.esteiraId} variante="inline" />
            )}
            {/* at-96: retorno de garantia — nesta tela distingue retrabalho de serviço novo. */}
            <SeloPosEvento posEvento={car?.posEvento} />
            <SeloPrazoDaEtapa car={car} />
            {/* at-99: nesta tela o selo explica um número — as horas de duas esteiras diferentes
                no mesmo atendimento, no mesmo período. Sem ele, a lista de atividades parece
                inconsistente com a etapa do veículo. */}
            <SeloEsteiraParalela
              esteirasParalelas={car?.esteirasParalelas}
              onClick={() => setControleParalelasAberto(true)}
            />
          </h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>

        <div className="controle-producao-modal-body">
          {loading && (
            <div className="controle-producao-modal-loading">
              <div className="spinner"></div>
              <p>Carregando detalhes do atendimento...</p>
            </div>
          )}

          {/* BUG-V76: o erro de carga precisa aparecer — antes o modal ficava em branco. */}
          {!loading && error && (
            <div className="controle-producao-modal-loading">
              <p>{error}</p>
              <p>Recarregue a página; se persistir, acione a TI.</p>
              <button
                className="controle-producao-modal-btn approve-btn"
                onClick={() => { setLoading(true); loadData(); }}
              >
                Tentar novamente
              </button>
            </div>
          )}

          {!loading && !error && !car && (
            <div className="controle-producao-modal-loading">
              <p>Não foi possível exibir este atendimento.</p>
            </div>
          )}

          {!loading && !error && car && (
            <div className="atendimento-modal-layout">
              {/* Coluna Esquerda: Informações Gerais e Atividades */}
              <div className="atendimento-modal-left-col">
                {/* Card do Veículo */}
                <div className="atendimento-vehicle-card">
                  <div className="atendimento-vehicle-card-header">
                    <div className="atendimento-placa-mercosul">
                      <div className="placa-header">BRASIL</div>
                      <div className="placa-body">{car.plate || '-'}</div>
                    </div>
                    <div className="atendimento-vehicle-header-info">
                      <span className="vehicle-model-title">{car.model || 'Modelo não especificado'}</span>
                      <span className="vehicle-class-subtitle">{car.carClass || 'Classe N/A'}</span>
                    </div>
                  </div>
                  <div className="atendimento-vehicle-card-details">
                    <div className="controle-producao-detail-item">
                      <span className="detail-label">Cor</span>
                      <span className="detail-value">{car.color || '-'}</span>
                    </div>
                    <div className="controle-producao-detail-item">
                      <span className="detail-label">Nº do Evento</span>
                      <span className="detail-value">{car.eventNumber ?? '-'}</span>
                    </div>
                    <div className="controle-producao-detail-item">
                      <span className="detail-label">Entrada</span>
                      <span className="detail-value">{formatDate(car.entryDate)}</span>
                    </div>
                    <div className="controle-producao-detail-item">
                      <span className="detail-label">Dias na oficina</span>
                      <span className="detail-value highlighted-days">{car.daysInShop ?? '0'} {car.daysInShop === 1 ? 'dia' : 'dias'}</span>
                    </div>
                    <div className="controle-producao-detail-item">
                      <span className="detail-label">Previsão</span>
                      <span className="detail-value">{car.deliveryForecast ? `${car.deliveryForecast} dias` : '-'}</span>
                    </div>
                    <div className="controle-producao-detail-item">
                      <span className="detail-label">Esteira atual</span>
                      <span className="detail-value current-lane-value">{esteiraAtualLabel}</span>
                    </div>
                  </div>
                  {/* at-54: texto livre do atendimento (componente compartilhado; some quando vazio) */}
                  <ObservacoesAtendimento
                    observacoesCliente={car.observacoesCliente}
                    observacoesOficina={car.observacoesOficina}
                  />

                  {/* at-89: o que já foi feito no veículo (some quando não há sub-tarefas) */}
                  <ProgressoSubtarefas carId={car.id} />
                  <ComentariosDoVeiculo carId={car.id} />
                </div>

                {/*
                  at-66 (BUG-V16): tempo trabalhado total e por setor.

                  Os dois já eram CALCULADOS aqui e nunca renderizados — só o contador de
                  pendências saía do `totals`. A ajuda da tela promete os dois ao Chefe, e a
                  soma é homem-hora (ADR 0020): dois colaboradores por uma hora somam duas
                  horas. Some quando não há atividade — não é ausência de dado, é atendimento
                  sem apontamento, e a lista logo abaixo já diz isso.
                */}
                {orderedSessions.length > 0 && (
                  <div className="atendimento-section">
                    <div className="atendimento-section-title">Tempo trabalhado</div>
                    <div className="atendimento-vehicle-card-details">
                      <div className="controle-producao-detail-item">
                        <span className="detail-label">Total no atendimento</span>
                        <span className="detail-value highlighted-days">{formatDuration(totals.totalWorked)}</span>
                      </div>
                      {totals.bySector
                        .slice()
                        .sort((left, right) => right[1] - left[1])
                        .map(([setor, segundos]) => (
                          <div className="controle-producao-detail-item" key={`setor-${setor}`}>
                            <span className="detail-label">{setor}</span>
                            <span className="detail-value">{formatDuration(segundos)}</span>
                          </div>
                        ))}
                    </div>
                    <p className="atendimento-totais-hint">
                      O total soma as horas de todos os colaboradores: dois colaboradores trabalhando uma hora
                      no mesmo veículo somam duas horas. Pausas já estão descontadas.
                    </p>
                  </div>
                )}

                {/* Atividades */}
                <div className="atendimento-section">
                  <div className="atendimento-section-title">
                    Atividades
                    {totals.pendingCount > 0 && (
                      <span className="atendimento-pending-badge">{totals.pendingCount} pendente(s)</span>
                    )}
                  </div>

                  {orderedSessions.length === 0 ? (
                    <div className="atendimento-empty-activities">
                      Nenhuma atividade registrada neste atendimento.
                    </div>
                  ) : (
                    <div className="atendimento-activities-list">
                      {orderedSessions.map((session) => {
                        const canApprove = isChefeView && session.status === 'FINISHED' && session.approvalStatus === 'PENDING';
                        const isApprovalOpen = approvalSessionId === session.id;
                        const isExpanded = expandedSessionId === session.id || isApprovalOpen;
                        const toggleExpanded = () => {
                          if (isExpanded) {
                            setExpandedSessionId(null);
                            if (isApprovalOpen) setApprovalSessionId(null);
                          } else {
                            setExpandedSessionId(session.id);
                          }
                        };

                        return (
                          <div key={session.id} className={`atendimento-activity ${session.status.toLowerCase()} ${session.approvalStatus.toLowerCase()}`}>
                            {/* Linha compacta — detalhes na expansão */}
                            <button type="button" className="atendimento-activity-row" onClick={toggleExpanded}>
                              <div className="activity-user-info">
                                <span className="activity-collaborator-name">{session.collaboratorName || 'Colaborador'}</span>
                                <span className="activity-sector-label">{labelDe(session.sector) || session.sector || '-'}</span>
                              </div>
                              <div className="activity-row-right">
                                {formatDuration(session.durationInSeconds) !== '-' && (
                                  <span className="activity-row-duration">{formatDuration(session.durationInSeconds)}</span>
                                )}
                                {/* Mesma paleta dos badges da timeline: laranja = ativo,
                                    amarelo = aguardando, verde = concluído, vermelho = recusado */}
                                <span className={`atendimento-pill ${session.status === 'IN_PROGRESS' ? 'pill-orange' : session.status === 'PAUSED' ? 'pill-yellow' : 'pill-green'}`}>
                                  {SESSION_STATUS_LABEL[session.status] || session.status}
                                </span>
                                {/* Aprovação só faz sentido após a sessão terminar — antes disso
                                    o "Pendente" é redundante e confunde. */}
                                {session.status === 'FINISHED' && (
                                  <span className={`atendimento-pill ${session.approvalStatus === 'APPROVED' ? 'pill-green' : session.approvalStatus === 'REJECTED' ? 'pill-red' : 'pill-yellow'}`}>
                                    {APPROVAL_STATUS_LABEL[session.approvalStatus] || session.approvalStatus}
                                  </span>
                                )}
                                {/* at-46: a DECLARAÇÃO do colaborador fica visível mesmo quando o
                                    serviço da esteira segue em andamento com um colega (last-one-wins)
                                    — o Chefe vê quem finalizou dizendo que não terminou. */}
                                {session.status === 'FINISHED' && session.servicoConcluido === false && (
                                  <span className="atendimento-pill pill-purple" title="O colaborador finalizou declarando que o serviço desta esteira não foi concluído">
                                    Serviço inacabado
                                  </span>
                                )}
                                <span className={`activity-row-chevron ${isExpanded ? 'open' : ''}`}>▼</span>
                              </div>
                            </button>

                            {isExpanded && (
                            <div className="atendimento-activity-details">
                            <div className="atendimento-activity-info-grid">
                              <div className="activity-info-item">
                                <span className="info-label">Data</span>
                                <span className="info-val">{formatDate(session.createdAt)}</span>
                              </div>
                              <div className="activity-info-item">
                                <span className="info-label">Início</span>
                                <span className="info-val">{session.startedAt ? new Date(session.startedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                              </div>
                              <div className="activity-info-item">
                                <span className="info-label">Fim</span>
                                <span className="info-val">{session.endedAt ? new Date(session.endedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '-'}</span>
                              </div>
                              <div className="activity-info-item">
                                <span className="info-label">Duração</span>
                                <span className="info-val duration-val">{formatDuration(session.durationInSeconds)}</span>
                              </div>
                              {(session.totalPausedSeconds ?? 0) > 0 && (
                                <>
                                  <div className="activity-info-item">
                                    <span className="info-label">Pausado</span>
                                    <span className="info-val">{formatDuration(session.totalPausedSeconds)}</span>
                                  </div>
                                  <div className="activity-info-item">
                                    <span className="info-label">Efetivo</span>
                                    <span className="info-val worked-val">{formatDuration(workedSeconds(session))}</span>
                                  </div>
                                </>
                              )}
                              {session.approvedByName && (
                                <div className="activity-info-item full-width-info">
                                  <span className="info-label">{session.approvalStatus === 'REJECTED' ? 'Reprovado por' : 'Aprovado por'}</span>
                                  <span className="info-val"><strong>{session.approvedByName}</strong></span>
                                </div>
                              )}
                            </div>

                            {session.rejectionReason && (
                              <div className="controle-producao-rejection-reason">
                                <span className="rejection-title">Motivo da Reprovação:</span>
                                <p className="rejection-text">{session.rejectionReason}</p>
                              </div>
                            )}

                            {session.pauses && session.pauses.length > 0 && (
                              <div className="atendimento-pauses">
                                <div className="pauses-title">Paradas registradas:</div>
                                {session.pauses.map((p, i) => (
                                  <div key={p.id} className="atendimento-pause-row">
                                    <div className="pause-reason-line">
                                      <strong>Pausa {i + 1}:</strong> {p.reason}
                                      {/* at-48: o Chefe precisa distinguir o desconto previsto
                                          (almoço/lanche) da pausa que o colaborador registrou. */}
                                      {p.automatica && (
                                        <span className="pause-automatica-badge">Automática</span>
                                      )}
                                    </div>
                                    <div className="pause-time-line">
                                      {new Date(p.pausedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                      {p.resumedAt ? ` → ${new Date(p.resumedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ' → Em pausa'}
                                      {p.pauseDurationInSeconds ? ` (${formatDuration(p.pauseDurationInSeconds)})` : ''}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {canApprove && !isApprovalOpen && (
                              <div className="activity-actions-wrapper">
                                <button className="controle-producao-btn-apply analyze-btn" onClick={() => openApproval(session)}>
                                  Analisar Horas
                                </button>
                              </div>
                            )}

                            {canApprove && isApprovalOpen && (
                              <div className="atendimento-approval-box">
                                {session.carPendingStatus && session.carStatusApprovalStatus === 'PENDING' && (
                                  <div className="controle-producao-next-sector-info">
                                    <span className="next-sector-label">Mudança de Esteira Solicitada</span>
                                    <div className="next-sector-flow">
                                      <span className="next-sector-from">{labelDe(session.sector) || session.sector}</span>
                                      <span className="next-sector-arrow">→</span>
                                      <span className="next-sector-to">{labelDe(session.carPendingStatus ?? '') || session.carPendingStatus}</span>
                                    </div>
                                    <div className="controle-producao-filter-field date-field-wrapper">
                                      <label>Dias estimados no próximo setor</label>
                                      <input
                                        type="number"
                                        min="1"
                                        placeholder="Ex: 3"
                                        value={approvalEstimatedDays}
                                        onChange={(e) => setApprovalEstimatedDays(e.target.value)}
                                        style={{ maxWidth: '160px' }}
                                      />
                                    </div>
                                  </div>
                                )}

                                <div className="controle-producao-filter-field select-field-wrapper">
                                  <label>Colaborador do veículo após esta decisão</label>
                                  {loadingCollaborators ? (
                                    <div className="loading-helper">Carregando colaboradores...</div>
                                  ) : (
                                    <select
                                      value={approvalNextCollaboratorId}
                                      onChange={(e) => setApprovalNextCollaboratorId(e.target.value)}
                                    >
                                      {/* at-93: "Não atribuir" é a PRIMEIRA opção e o padrão —
                                          o vocabulário é o mesmo dos modais de esteira
                                          (`shared/ui/escolha-colaborador.ts`). */}
                                      <option value={LEAVE_UNASSIGNED}>
                                        Não atribuir — o veículo segue sem colaborador
                                      </option>
                                      <option value="">Manter os colaboradores atuais</option>
                                      {/* 15/09/2026: só quem atende a esteira relevante (a
                                          sugerida, quando há mudança pendente, ou a atual) e as
                                          abertas em paralelo. Antes os demais vinham
                                          desabilitados, com o motivo ao lado. */}
                                      {colaboradoresOferecidos.map((c) => (
                                        <option key={c.id} value={c.id}>
                                          {c.fullName || c.username || 'Colaborador'}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                  {/* Com a lista restrita, o campo com só duas opções não explica
                                      por que ninguém aparece — e "não tem colaborador" seria a
                                      leitura errada. O que falta é especialidade cadastrada. */}
                                  {!loadingCollaborators &&
                                    idsElegiveisProximo !== null &&
                                    colaboradoresOferecidos.length === 0 && (
                                      <div className="loading-helper">
                                        Nenhum colaborador tem a especialidade desta esteira, então o
                                        veículo seguirá sem colaborador. Para liberar alguém, cadastre
                                        a especialidade em Administração do Sistema.
                                      </div>
                                    )}
                                </div>

                                <div className="controle-producao-rejection-input-group">
                                  <label>Justificativa (opcional)</label>
                                  <input
                                    type="text"
                                    placeholder="Digite o motivo da reprovação..."
                                    value={rejectionReason}
                                    onChange={(e) => setRejectionReason(e.target.value)}
                                  />
                                </div>

                                <div className="controle-producao-modal-action-buttons">
                                  <button
                                    className="controle-producao-modal-btn reject-btn"
                                    onClick={() => handleReject(session)}
                                    disabled={rejectMutation.isPending}
                                  >
                                    Reprovar Horas
                                  </button>
                                  <button
                                    className="controle-producao-modal-btn approve-btn"
                                    onClick={() => handleApprove(session)}
                                    disabled={approveMutation.isPending}
                                  >
                                    Aprovar Horas
                                  </button>
                                </div>
                              </div>
                            )}
                            </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Coluna Direita: Histórico de esteiras */}
              <div className="atendimento-modal-right-col">
                <div className="atendimento-section">
                  <div className="atendimento-section-title">Histórico de esteiras</div>
                  {orderedHistory.length === 0 ? (
                    <div style={{ color: 'var(--tema-texto-3, #6b7280)', fontSize: '13px' }}>Nenhuma movimentação registrada.</div>
                  ) : (
                    <div className="atendimento-timeline">
                      {orderedHistory.map((entry: any) => {
                        const isCurrent = entry.validTo == null;
                        const isPending = entry.approvalStatus === 'PENDING';
                        const isRejected = entry.approvalStatus === 'REJECTED';
                        // Toda linha tem exatamente um badge (decisão de 07/2026):
                        // Pendente > Recusada > Atual > Inacabado > Concluída.
                        // at-46: um período fechado cujo serviço ficou PAUSADA não é "Concluída" —
                        // o veículo saiu da esteira sem terminar o serviço.
                        const servicoInacabado = (car?.servicosInacabados ?? []).some(
                          (s: ServicoInacabado) => s.estado === 'PAUSADA' && s.esteiraId === entry.esteiraId,
                        );
                        const badge = isPending
                          ? { className: 'timeline-pending-badge', label: 'Pendente' }
                          : isRejected
                            ? { className: 'timeline-rejected-badge', label: 'Recusada' }
                            : isCurrent
                              ? { className: 'timeline-current-badge', label: 'Atual' }
                              : servicoInacabado
                                ? { className: 'timeline-unfinished-badge', label: 'Inacabado' }
                                : { className: 'timeline-done-badge', label: 'Concluída' };
                        const nodeState = isPending
                          ? 'pending'
                          : isRejected
                            ? 'rejected'
                            : isCurrent
                              ? 'current'
                              : 'done';
                        return (
                          <div key={entry.id} className={`atendimento-timeline-node ${nodeState}`}>
                            <div className="timeline-marker"></div>
                            <div className="timeline-content">
                              <div className="timeline-header">
                                <span className="timeline-esteira">
                                  {labelDe(entry.esteiraId || entry.status) || entry.status || '-'}
                                </span>
                                <div className="timeline-badges-group">
                                  <span className={badge.className}>{badge.label}</span>
                                </div>
                              </div>
                              <div className="timeline-dates">
                                {formatDateTime(entry.validFrom)} → {isCurrent ? 'atual' : formatDateTime(entry.validTo)}
                              </div>
                              <div className="timeline-duration">
                                Tempo: <strong>{isCurrent ? 'em andamento' : formatDuration(entry.secondsInStatus)}</strong>
                                {entry.changedByName && <span> • Por: <strong>{entry.changedByName}</strong></span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmModalData && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen
            message={confirmModalData.message}
            confirmLabel="Sim, tenho certeza"
            onConfirm={confirmModalData.onConfirm}
            onCancel={() => setConfirmModalData(null)}
          />
        </div>
      )}

      {/* at-99: controle das esteiras em paralelo, aberto pelo selo ao lado da placa. O `div` com
          `stopPropagation` é o mesmo cuidado do modal de confirmação acima: este modal fecha por
          clique no overlay, e sem ele o primeiro clique aqui dentro fecharia os dois. */}
      {controleParalelasAberto && car && (
        <div onClick={(e) => e.stopPropagation()}>
          <ControleParalelasModal
            carId={carId}
            plate={car.plate}
            esteiraPrincipalId={car.esteira?.id ?? car.esteiraId}
            esteiraPrincipalNome={car.esteira?.nome ?? labelDe(car.esteiraId)}
            principalEhDeTrabalho={car.esteira ? car.esteira.sistema === false : undefined}
            esteirasParalelas={car.esteirasParalelas}
            esteirasLiberadasIds={(car.esteirasLiberadas ?? []).map((l: any) => l.esteiraId)}
            liberacaoTotal={Boolean(car.releasedAt)}
            onCancel={() => setControleParalelasAberto(false)}
            onAlterado={() => {
              void loadData();
              onChanged?.();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default AtendimentoDetailsModal;
